/**
 * Extract per-bucket, per-iteration statistics from a benchmark profile into a
 * compact intermediate JSON file suitable for cross-profile comparison.
 *
 * The output is intentionally sparse: only (suite, bucket) pairs with nonzero
 * weight are stored. At 200 iterations × 10578 buckets × 20 suites a dense
 * representation would be ~323 MB; the sparse form is ~2 MB.
 *
 * Usage:
 *   yarn build-node-tools
 *   node node-tools-dist/extract-benchmark-stats.js \
 *     --input ~/Downloads/profile.json \
 *     --output /tmp/profile-stats.json
 */

import {
  computeBenchmarkScores,
  computeIterationMarkersAndMeasuredSamples,
  getBenchmarkInfo,
} from 'firefox-profiler/profile-logic/benchmark/benchmark-stuff';
import type {
  BenchmarkHarness,
  SamplesTableForThisStuff,
} from 'firefox-profiler/profile-logic/benchmark/benchmark-stuff';
import {
  correlateIPCMarkers,
  deriveMarkersFromRawMarkerTable,
} from 'firefox-profiler/profile-logic/marker-data';
import {
  computeTimeColumnForRawSamplesTable,
  getTimeRangeForThread,
} from 'firefox-profiler/profile-logic/profile-data';
import { StringTable } from 'firefox-profiler/utils/string-table';
import { FuncFlag } from 'firefox-profiler/types/profile';

import type {
  IndexIntoFuncTable,
  IndexIntoStackTable,
  Profile,
  RawProfileSharedData,
} from '../../types/profile';

// ---------------------------------------------------------------------------
// Types for the intermediate JSON
// ---------------------------------------------------------------------------

/** One (suite, bucket) pair with nonzero weight. */
export type SparseBucketEntry = {
  /** Index into the profile's global bucket list. */
  bucketIndex: number;
  /** Weight sum per iteration, length = iterationCount. Held as a typed
   * subarray of the underlying `bucketIterationTotals` buffer in the browser
   * path (no copy). CLI-loaded stats parse back to `number[]` — both types
   * support indexed access/assignment and `.slice()`. */
  iterationTotals: Float64Array | number[];
};

export type SuiteStats = {
  suiteName: string;
  iterationCount: number;
  /** Only buckets that have nonzero total weight across all iterations. */
  buckets: SparseBucketEntry[];
};

export type ProfileBenchmarkStats = {
  /** Name of each bucket (JS function name or similar). Length = total bucket count. */
  bucketNames: string[];
  /**
   * Cross-engine matching key for each bucket. Same length as bucketNames.
   *
   * For JS funcs, this is `<filename>:<startLine>:<startColumn>` (using the
   * source/lineNumber/columnNumber columns of the funcTable). Function names
   * differ across engines for the same logical function — V8 reports
   * `Template.show` where SpiderMonkey reports `Template.prototype.show`, and
   * anonymous-arrow naming heuristics diverge entirely — but the source
   * location is stable, so matching on it lets `compareBuckets` align the
   * two engines' samples for the same function.
   *
   * For non-JS funcs (DOM-binding `relevantForJS` shims, inserted `Label`
   * funcs, etc.), the key is the name itself: those are already engine-
   * neutral by design, apart from capitalisation, which `compareBuckets`
   * ignores for name keys.
   *
   * Falls back to the name if a JS func has no source/line/col (e.g. the
   * profile wasn't run through `--canonicalize-js-location`).
   */
  bucketKeys: string[];
  /**
   * Func index (in profile.shared.funcTable) for each bucket. Same length as
   * bucketNames. -1 for the synthetic "no JS frame" bucket. Useful when callers
   * want to reach back into the source profile for a given bucket, e.g. to feed
   * a focusSelf() flame graph.
   */
  bucketFuncs: Array<IndexIntoFuncTable>;
  /**
   * Whether each bucket is a JS function, as opposed to a DOM binding, a label
   * or the synthetic "no JS frame" bucket. Same length as bucketNames. Used to
   * sum every JS function into one row; see `jsFunctionIterationTotals`.
   *
   * Absent from stats files written before it was added.
   */
  bucketIsJS?: boolean[];
  /** Per-suite sparse bucket data. This is the only per-bucket weight data
   * stored; the geomean-normalised "global" view across all suites is derived
   * from it at comparison time by `computeGlobalBuckets`, because the
   * normalisation factors have to be shared between the two profiles being
   * compared rather than computed per profile. */
  suites: SuiteStats[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeJsOnlySampleBuckets(
  shared: RawProfileSharedData,
  sampleStacks: Array<IndexIntoStackTable | null>
): {
  bucketFuncs: Array<IndexIntoFuncTable>;
  sampleBuckets: Int32Array<ArrayBuffer>;
} {
  const { funcTable, stackTable, frameTable } = shared;
  const bucketFuncs = new Array<IndexIntoFuncTable>();
  const funcIndexToBucketIndex = new Map<IndexIntoFuncTable, number>();

  const stackIndexToJsOnlyFuncIndex = new Int32Array(stackTable.length);
  for (let stackIndex = 0; stackIndex < stackTable.length; stackIndex++) {
    const frameIndex = stackTable.frame[stackIndex];
    const funcIndex = frameTable.func[frameIndex];
    if (
      (funcTable.flags[funcIndex] &
        (FuncFlag.IsJS | FuncFlag.RelevantForJS)) !==
      0
    ) {
      stackIndexToJsOnlyFuncIndex[stackIndex] = funcIndex;
    } else {
      const prefixOffset = stackTable.prefixOffset[stackIndex];
      if (prefixOffset !== 0) {
        const parentStackIndex = stackIndex - prefixOffset;
        stackIndexToJsOnlyFuncIndex[stackIndex] =
          stackIndexToJsOnlyFuncIndex[parentStackIndex];
      } else {
        stackIndexToJsOnlyFuncIndex[stackIndex] = -1;
      }
    }
  }

  const sampleBuckets = new Int32Array(sampleStacks.length);
  for (let sampleIndex = 0; sampleIndex < sampleBuckets.length; sampleIndex++) {
    const stackIndex = sampleStacks[sampleIndex];
    if (stackIndex !== null) {
      const jsOnlyFuncIndex = stackIndexToJsOnlyFuncIndex[stackIndex];
      let bucketIndex =
        jsOnlyFuncIndex !== -1
          ? funcIndexToBucketIndex.get(jsOnlyFuncIndex)
          : -1;
      if (bucketIndex === undefined) {
        bucketIndex = bucketFuncs.length;
        bucketFuncs[bucketIndex] = jsOnlyFuncIndex;
        funcIndexToBucketIndex.set(jsOnlyFuncIndex, bucketIndex);
      }
      sampleBuckets[sampleIndex] = bucketIndex;
    } else {
      sampleBuckets[sampleIndex] = -1;
    }
  }

  return { bucketFuncs, sampleBuckets };
}

// ---------------------------------------------------------------------------
// Main extraction logic
// ---------------------------------------------------------------------------

/**
 * Extract per-bucket, per-iteration statistics from an already-loaded Profile.
 * This is the browser-safe core of the extraction logic; it has no I/O dependencies.
 */
export function extractBenchmarkStatsFromProfile(
  profile: Profile,
  benchmarkHarness: BenchmarkHarness = 'speedometer'
): ProfileBenchmarkStats {
  const benchmarkInfo = getBenchmarkInfo(profile, benchmarkHarness);
  const { shared } = profile;
  const thread = profile.threads[benchmarkInfo.threadIndex];

  const { markers } = deriveMarkersFromRawMarkerTable(
    thread.markers,
    shared.stringArray,
    thread.tid,
    getTimeRangeForThread(thread, profile.meta.interval),
    correlateIPCMarkers(profile.threads, shared)
  );
  const stringTable = StringTable.withBackingArray(shared.stringArray);

  const sampleCount = thread.samples.length;
  const { sampleBuckets, bucketFuncs } = computeJsOnlySampleBuckets(
    shared,
    thread.samples.stack
  );

  const profileOverheadBucket = bucketFuncs.findIndex(
    (func) =>
      shared.stringArray[shared.funcTable.name[func]] === 'Profiling overhead'
  );
  const bucketsToIgnore =
    profileOverheadBucket !== -1 ? [profileOverheadBucket] : [];

  const samples: SamplesTableForThisStuff = {
    length: sampleCount,
    time: new Float64Array(computeTimeColumnForRawSamplesTable(thread.samples)),
    weight: thread.samples.weight
      ? new Float64Array(thread.samples.weight)
      : new Float64Array(sampleCount).fill(1),
    bucketIndex: sampleBuckets,
    bucketCount: bucketFuncs.length,
  };

  const iterationMarkersAndMeasuredSamples =
    computeIterationMarkersAndMeasuredSamples(
      benchmarkInfo,
      markers,
      samples,
      stringTable,
      bucketsToIgnore
    );

  const benchmarkScores = computeBenchmarkScores(
    iterationMarkersAndMeasuredSamples
  );

  const { funcTable: sharedFuncTable, sources: sharedSources } = shared;
  const bucketNames = bucketFuncs.map(
    (funcIndex) => shared.stringArray[sharedFuncTable.name[funcIndex]]
  );
  // Build cross-engine matching keys: filename:line:col for JS funcs with a
  // source, name otherwise. See ProfileBenchmarkStats.bucketKeys for the
  // motivation (function names diverge across engines for the same source
  // location).
  const bucketKeys = bucketFuncs.map((funcIndex, b) => {
    const flags = sharedFuncTable.flags[funcIndex];
    const required =
      FuncFlag.IsJS |
      FuncFlag.HasSource |
      FuncFlag.HasLine |
      FuncFlag.HasColumn;
    if ((flags & required) === required) {
      const sourceIndex = sharedFuncTable.source[funcIndex];
      const line = sharedFuncTable.lineNumber[funcIndex];
      const col = sharedFuncTable.columnNumber[funcIndex];
      const filename = shared.stringArray[sharedSources.filename[sourceIndex]];
      return `${filename}:${line}:${col}`;
    }
    return bucketNames[b];
  });

  const bucketIsJS = bucketFuncs.map(
    (funcIndex) =>
      funcIndex !== -1 &&
      (sharedFuncTable.flags[funcIndex] & FuncFlag.IsJS) !== 0
  );

  const bucketCount = bucketFuncs.length;
  const { allSuiteScores } = benchmarkScores;

  // Build per-suite sparse entries
  const suites: SuiteStats[] = allSuiteScores.map((suiteScores) => {
    const iterationCount = suiteScores.bucketStats!.iterationCount;
    const buckets: SparseBucketEntry[] = [];

    for (let b = 0; b < bucketCount; b++) {
      if (suiteScores.bucketTotals[b] === 0) {
        continue;
      }
      const base = b * iterationCount;
      const iterationTotals = suiteScores.bucketIterationTotals.subarray(
        base,
        base + iterationCount
      );
      buckets.push({ bucketIndex: b, iterationTotals });
    }

    return {
      suiteName: suiteScores.suiteName,
      iterationCount,
      buckets,
    };
  });

  return { bucketNames, bucketKeys, bucketFuncs, bucketIsJS, suites };
}
